import { useCallback, useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { KIND_ORDER, KIND_STYLE, weekHeadingColor } from './constants.js';
import type { PoolWeek } from './constants.js';
import type { BlockKind, CandidateCase } from './types.js';

/** CandidatePool の props。 */
export interface CandidatePoolProps {
  /** 表示対象チームの候補案件。 */
  candidates: CandidateCase[];
  /** 週セクション定義（4 週分）。 */
  weeks: PoolWeek[];
  /** ドラッグ中にハイライトする週セクション（ドロップ先予告）。null なら無し。 */
  hoverWeekIndex: number | null;
  /** 白板に配置済みの候補案件 ID 集合（実心グレーで「配置済み」を表す）。 */
  arrangedIds: Set<string>;
  /**
   * 閲覧者が白板を編集できるか。false のときは追加フォーム・一括取込・削除・ドラッグの
   * 導線を出さず、候補の一覧として読むだけの表示にする。
   */
  canEdit: boolean;
  /** 追加フォームの送信通知（配置先は親が決定。現状は未配置ブロックとしてトレイに生成）。 */
  onAddCandidate: (input: { title: string; sizePt: number; kind: BlockKind }) => void;
  /** 候補案件を削除する。 */
  onDeleteCandidate: (id: string) => void;
  /** 候補案件をドラッグ開始する（ポインタダウン）。白板 or 別の週セクションへドロップできる。 */
  onCandidatePointerDown: (candidate: CandidateCase, e: ReactPointerEvent) => void;
  /** チケット一括取込モーダルを開く。 */
  onOpenTicketImport: () => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox の候補案件プール（canEdit=false では追加・取込・削除・ドラッグの導線を出さない閲覧専用表示になる）。着手予定週ごとに 4 セクションへ分けて表示し、各週の合計 pt を出す。案件はフォームから追加・削除でき、白板へ直接ドラッグ配置、または週セクション間をドラッグ移動できる。ヘッダーからは案件番号によるチケット一括取込も開ける
 * @graph-connects none
 */
export function CandidatePool({
  candidates,
  weeks,
  hoverWeekIndex,
  arrangedIds,
  canEdit,
  onAddCandidate,
  onDeleteCandidate,
  onCandidatePointerDown,
  onOpenTicketImport,
}: CandidatePoolProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [size, setSize] = useState('8');
  const [kind, setKind] = useState<BlockKind>('dev');

  const sizePt = Number(size);
  const canAdd = title.trim() !== '' && Number.isFinite(sizePt) && sizePt > 0;

  const handleAdd = useCallback(() => {
    const trimmed = title.trim();
    if (trimmed === '' || !Number.isFinite(sizePt) || sizePt <= 0) return;
    onAddCandidate({ title: trimmed, sizePt, kind });
    setTitle('');
    setSize('8');
    setKind('dev');
  }, [title, sizePt, kind, onAddCandidate]);

  // 週インデックス → その週の候補・合計 pt。
  const byWeek = useMemo(() => {
    const map = new Map<number, { items: CandidateCase[]; totalPt: number }>();
    for (const w of weeks) map.set(w.index, { items: [], totalPt: 0 });
    for (const c of candidates) {
      const bucket = map.get(c.weekIndex);
      if (bucket === undefined) continue;
      bucket.items.push(c);
      bucket.totalPt += c.sizePt;
    }
    return map;
  }, [candidates, weeks]);

  return (
    <div className="flex flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-sm">
      {/* ヘッダー: タイトル + 追加フォーム（種類 > サイズ > 名称、week0 に追加） */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[var(--color-border)] px-3 py-2">
        <div className="mr-auto">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('pjSandbox.pool.title')}
          </h3>
          <p className="text-[11px] text-[var(--color-text-muted)]">{t('pjSandbox.pool.hint')}</p>
        </div>
        {canEdit && (
          <>
            <div className="flex items-center gap-1">
              {KIND_ORDER.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setKind(k);
                  }}
                  className="rounded-md border px-2 py-1 text-xs transition-colors"
                  style={{
                    borderColor: kind === k ? KIND_STYLE[k].fill : 'var(--color-border)',
                    backgroundColor: kind === k ? KIND_STYLE[k].fill : 'transparent',
                    color: kind === k ? KIND_STYLE[k].text : 'var(--color-text-secondary)',
                  }}
                >
                  {t(KIND_STYLE[k].i18nKey)}
                </button>
              ))}
            </div>
            <input
              type="number"
              value={size}
              onChange={(e) => {
                setSize(e.target.value);
              }}
              aria-label={t('pjSandbox.palette.size')}
              className="w-14 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
            />
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
              placeholder={t('pjSandbox.pool.addPlaceholder')}
              aria-label={t('pjSandbox.pool.addPlaceholder')}
              className="w-40 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="rounded-md bg-[var(--color-accent-primary)] px-3 py-1.5 text-sm font-bold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('pjSandbox.pool.add')}
            </button>
            <button
              type="button"
              onClick={onOpenTicketImport}
              className="rounded-md bg-[var(--color-backlog-action)] px-3 py-1.5 text-sm font-bold text-[var(--color-backlog-action-text)] transition-[filter] hover:brightness-95"
            >
              {t('pjSandbox.pool.importByNumber')}
            </button>
          </>
        )}
      </div>

      {/* 週セクションを未来4週で並べる。モバイルファースト: 既定は縦積み、sm で2列、lg で横4カラム。 */}
      <div className="grid grid-cols-1 gap-2 p-2 sm:grid-cols-2 lg:grid-cols-4">
        {weeks.map((w) => {
          const bucket = byWeek.get(w.index) ?? { items: [], totalPt: 0 };
          const isHover = hoverWeekIndex === w.index;
          // 当週（index 0）が最も濃く、先の週ほど淡くなる青の濃淡。
          const headingColor = weekHeadingColor(w.index);
          return (
            <div
              key={w.index}
              data-pj-week={w.index}
              className="flex min-w-0 flex-col rounded-md border p-2 transition-colors"
              style={{
                borderColor: isHover ? 'var(--color-accent-primary)' : 'var(--color-border)',
                backgroundColor: isHover ? 'rgba(99,102,241,0.08)' : 'transparent',
              }}
            >
              <div className="mb-1.5 flex items-center justify-between gap-1">
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{
                    backgroundColor: headingColor.bg,
                    color: headingColor.text,
                  }}
                >
                  {t('pjSandbox.pool.weekHeading', { date: w.label })}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)]">
                  {t('pjSandbox.pool.weekTotal', { pt: bucket.totalPt })}
                </span>
              </div>
              <div className="flex max-h-44 flex-col gap-1.5 overflow-y-auto">
                {bucket.items.length === 0 ? (
                  <p className="py-3 text-center text-[11px] text-[var(--color-text-muted)]">
                    {t('pjSandbox.pool.sectionEmpty')}
                  </p>
                ) : (
                  bucket.items.map((c) => {
                    const arranged = arrangedIds.has(c.id);
                    return (
                      <div
                        key={c.id}
                        role="button"
                        tabIndex={0}
                        onPointerDown={(e) => {
                          onCandidatePointerDown(c, e);
                        }}
                        className={`group flex items-center gap-1.5 rounded-md border px-2 py-1.5 transition-colors ${
                          canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
                        } ${
                          arranged
                            ? 'border-transparent'
                            : 'border-[var(--color-border)] bg-[var(--color-bg-base)] hover:border-[var(--color-border-hover)]'
                        }`}
                        style={{
                          touchAction: 'none',
                          ...(arranged ? { backgroundColor: '#4B5563' } : {}),
                        }}
                        title={
                          arranged
                            ? t('pjSandbox.pool.arranged')
                            : canEdit
                              ? t('pjSandbox.pool.dragHint')
                              : undefined
                        }
                      >
                        <span
                          className="h-3 w-3 shrink-0 rounded-sm"
                          style={{
                            backgroundColor: arranged ? '#9CA3AF' : KIND_STYLE[c.kind].fill,
                          }}
                        />
                        <span
                          className={`min-w-0 flex-1 truncate text-sm ${
                            arranged ? 'text-[#E5E7EB]' : 'text-[var(--color-text-primary)]'
                          }`}
                        >
                          {c.title}
                        </span>
                        {arranged && (
                          <span className="shrink-0 rounded-sm bg-black/25 px-1 text-[10px] font-medium text-[#E5E7EB]">
                            {t('pjSandbox.pool.arranged')}
                          </span>
                        )}
                        <span
                          className={`shrink-0 text-xs tabular-nums ${
                            arranged ? 'text-[#D1D5DB]' : 'text-[var(--color-text-muted)]'
                          }`}
                        >
                          {c.sizePt}pt
                        </span>
                        {canEdit && (
                          <button
                            type="button"
                            onPointerDown={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={() => {
                              onDeleteCandidate(c.id);
                            }}
                            className="shrink-0 rounded-full px-1 text-xs text-[var(--color-text-muted)] opacity-0 transition-opacity hover:bg-black/20 group-hover:opacity-100"
                            aria-label={t('pjSandbox.pool.deleteCandidate')}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
